/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { colors, DataTable, Heading, HFlow, Icon, TableFooter, Text, Tooltip, VFlow } from 'bold-ui'
import { Ellipsis } from 'components/Ellipsis'
import { InfoIcon } from 'components/InfoIcon'
import { HideOnScreenSize } from 'components/responsive'
import { TableBox } from 'components/table'
import { isSameDay, toDate } from 'date-fns'
import { ListaRegulacaoQuery, PrioridadeRegulacaoEnum, TipoRegulacaoEnum } from 'graphql/types.generated'
import { useServerTime } from 'hooks/useServerTime'
import { Fragment } from 'react'
import { formatDateAndHoursMinutes, formatHoursMinutes } from 'util/date/formatDate'
import { humanizeDateTime } from 'util/date/humanize-date'
import StatusSquare from 'view/atendimentos/detail/historico/components/StatusSquare'

import {
  ListaRegulacaoFilterModel,
  ListaRegulacaoTableItem,
  regulacaoClassificacaoPrioridadeRecord,
  RegulacaoEnumTextMap,
  StatusRegulacaoEnumMap,
} from '../model-regulacao'
import { ListaRegulacaoTableAction } from './ListaRegulacaoTableAction'
import { ListaRegulacaoTableFilter } from './ListaRegulacaoTableFilter'

interface ListaRegulacaoTableProps {
  data: ListaRegulacaoQuery
  loading: boolean
  filter: ListaRegulacaoFilterModel
  updateFilter(filter: ListaRegulacaoFilterModel): void
}

export const ListaRegulacaoTable = (props: ListaRegulacaoTableProps) => {
  const { data, loading, filter, updateFilter } = props

  const { getServerTimeNow } = useServerTime()
  const now = getServerTimeNow()

  return (
    <VFlow vSpacing={1} style={styles.line}>
      <Heading level={1} fontWeight='bold'>
        Solicitações em processo de regulação
      </Heading>
      <Heading level={4} fontWeight='normal'>
        Cidadãos vinculados à <strong>{renderFiltrosVinculadosA(filter)}</strong>.
      </Heading>
      <ListaRegulacaoTableFilter filter={filter} updateFilter={updateFilter} />
      <TableBox>
        <DataTable<ListaRegulacaoTableItem>
          loading={loading}
          rows={data.listaRegulacao?.content || []}
          columns={[
            {
              name: 'statusGradient',
              render: renderStatusGradient,
              style: [styles.tableCell, styles.tableCellGradient],
            },
            {
              name: 'status',
              header: 'Status na regulação',
              render: (item) => renderStatus(item, now),
              style: [styles.tableCell, styles.tableCellStatus],
            },
            {
              name: 'prioridade',
              header: renderHeaderPrioridade,
              render: renderPrioridade,
              style: styles.tableCell,
            },
            {
              name: 'solicitacao',
              header: 'Solicitação',
              render: renderSolicitacao,
              style: styles.tableCell,
            },
            {
              name: 'cidadao',
              header: 'Cidadão',
              render: renderCidadao,
              style: styles.tableCell,
            },
            {
              name: 'solicitante',
              header: 'Solicitante',
              render: renderSolicitante,
              style: styles.tableCell,
            },
            {
              name: 'visualizar',
              header: '',
              render: (row) => <ListaRegulacaoTableAction item={row} />,
              style: styles.tableCell,
            },
          ]}
        />
        <TableFooter {...data.listaRegulacao?.pageInfo} sizeOptions={[5, 10, 30, 50]} style={styles.tableFooter} />
      </TableBox>
    </VFlow>
  )
}

const renderStatusGradient = (item: ListaRegulacaoTableItem) => {
  const { colorScale } = StatusRegulacaoEnumMap[item.ultimaEvolucao.statusRegulacao]
  return <div css={styles.statusGradient(colorScale.c30, colorScale.c60)} />
}

const renderStatus = (item: ListaRegulacaoTableItem, now: Date) => {
  const { icon: icone, mainColor, text } = StatusRegulacaoEnumMap[item.ultimaEvolucao.statusRegulacao]
  const dataEvolucao = item.ultimaEvolucao.dataEvolucao
  const isEvolucaoToday = isSameDay(toDate(dataEvolucao), now)

  return (
    <VFlow vSpacing={0.125}>
      <Text>
        {isEvolucaoToday ? `Hoje às ${formatHoursMinutes(dataEvolucao)}` : formatDateAndHoursMinutes(dataEvolucao)}
      </Text>
      <HFlow hSpacing={0.5} alignItems='center'>
        <Icon icon={icone} color={mainColor} size={1} />
        <Text>{text}</Text>
      </HFlow>
    </VFlow>
  )
}

const renderHeaderPrioridade = (
  <HFlow hSpacing={0.25} alignItems='center'>
    <Text>Prioridade</Text>
    <InfoIcon icon='infoCircleFilled' text='Prioridade definida no processo de regulação' />
  </HFlow>
)

const renderPrioridade = (item: ListaRegulacaoTableItem) => {
  const { mainColor, label } = regulacaoClassificacaoPrioridadeRecord[item.ultimaEvolucao.prioridade]

  if (item.ultimaEvolucao.prioridade === PrioridadeRegulacaoEnum.NAO_INFORMADA) {
    return (
      <Fragment>
        <HideOnScreenSize down='lg'>
          <Text color='secondary'>{label}</Text>
        </HideOnScreenSize>
        <HideOnScreenSize up='lg'>
          <Text
            color='secondary'
            style={css`
              display: flex;
              text-align: center;
            `}
          >
            {label}
          </Text>
        </HideOnScreenSize>
      </Fragment>
    )
  }

  return (
    <Fragment>
      <HideOnScreenSize down='lg'>
        <StatusSquare color={mainColor} description={label} />
      </HideOnScreenSize>
      <HideOnScreenSize up='lg'>
        <HFlow justifyContent='center' alignItems='center'>
          <StatusSquare color={mainColor} description={label} descriptionAsTooltip />
        </HFlow>
      </HideOnScreenSize>
    </Fragment>
  )
}

const renderUbs = (nome: string) => (
  <Fragment>
    <HideOnScreenSize down='lg'>
      <HFlow hSpacing={0.2} alignItems='center'>
        <Icon icon='hospital' size={1} />
        <Ellipsis maxLines={1}>{nome}</Ellipsis>
      </HFlow>
    </HideOnScreenSize>
    <HideOnScreenSize up='lg'>
      <HFlow hSpacing={0.2} alignItems='center'>
        <Tooltip text={nome}>
          <Icon icon='hospital' size={1} />
        </Tooltip>
      </HFlow>
    </HideOnScreenSize>
  </Fragment>
)

const renderSolicitacao = (item: ListaRegulacaoTableItem) => {
  let text: string
  let date: LocalDate

  if (item.tipoRegulacao === TipoRegulacaoEnum.GUIA_ENCAMINHAMENTO) {
    const guia = item.guiaEncaminhamento
    text = guia.cbo.nome
    date = guia.atendimentoProfissional.finalizadoEm
  } else {
    const exame = item.exame.exame
    text = `${exame.descricao} - ${exame.codigo}`
    date = item.exame.atendimentoProfissionalSolicitacao.finalizadoEm
  }

  return (
    <VFlow vSpacing={0}>
      <Ellipsis maxLines={3}>
        <Text fontWeight='bold'>{RegulacaoEnumTextMap[item.tipoRegulacao]} </Text>
        <Text>{text}</Text>
      </Ellipsis>
      {date && (
        <Ellipsis maxLines={1}>
          <Text>Solicitado há {humanizeDateTime(date)}</Text>
        </Ellipsis>
      )}
    </VFlow>
  )
}

const renderCidadao = (item: ListaRegulacaoTableItem) => {
  const cidadao = item.prontuario.cidadao
  const vinculacaoEquipe = cidadao.cidadaoVinculacaoEquipe

  return (
    <VFlow vSpacing={0}>
      <Ellipsis maxLines={1} fontWeight='bold'>
        {cidadao.nome}
      </Ellipsis>
      {vinculacaoEquipe?.equipe ? <Ellipsis maxLines={1}>{vinculacaoEquipe.equipe.nome}</Ellipsis> : 'Sem equipe'}
      {vinculacaoEquipe?.unidadeSaude && renderUbs(vinculacaoEquipe.unidadeSaude.nome)}
    </VFlow>
  )
}

const renderSolicitante = (item: ListaRegulacaoTableItem) => {
  const lotacao = item.lotacao

  return (
    <VFlow vSpacing={0}>
      <Ellipsis maxLines={1} fontWeight='bold'>
        {lotacao.profissional.nome}
      </Ellipsis>
      <Ellipsis maxLines={1}>{lotacao.cbo.nome}</Ellipsis>
      {lotacao.equipe && <Ellipsis maxLines={1}>{lotacao.equipe.nome}</Ellipsis>}
      {renderUbs(lotacao.unidadeSaude.nome)}
    </VFlow>
  )
}

const renderFiltrosVinculadosA = (filtros: ListaRegulacaoFilterModel) => {
  const {
    tableFilter: { equipe, unidadeSaude },
  } = filtros

  const filtroEquipe = equipe?.nome
  const filtroUbs = unidadeSaude?.nome

  return filtroEquipe || filtroUbs ? [filtroUbs, filtroEquipe].filterNotNull().join(' | ') : 'nenhum filtro aplicado'
}

const styles = {
  statusGradient: (from: string, to: string) => css`
    width: 0.5rem;
    height: 100%;
    background: linear-gradient(to top, ${from}, ${to});
  `,
  tableCell: css`
    padding: 0.5rem 1rem;
  `,
  tableCellGradient: css`
    padding: 0;
    width: 0.5rem;
    &:is(td) {
      border-bottom: 0;
    }
  `,
  tableCellStatus: css`
    padding-left: 0.5rem;
  `,
  tableFooter: css`
    border-top: 0;
  `,
  line: css`
    padding-top: 1rem;
    border-top: 1px solid ${colors.gray.c80};
  `,
}
